import { useQuery } from '@tanstack/react-query'

/**
 * `GET /api/max/me/polls-ref` — ref-context для персонализации онбординга + whitelist гейт.
 * B contract-doc §2.6, deployed 2026-07-27.
 *
 * `refSource=null` → юзер не пришёл через polls. Все поля кроме `isPollsWhitelisted` пустые.
 * `refPollId` есть, но `sourcePoll=null` → poll orphaned/удалён → F показывает generic copy.
 * `channelTitle=null` → приватный/удалённый канал → F fallback «канала N».
 * `isPollsWhitelisted` — single source of truth для UI-гейтов (заменяет локальный POLLS_WHITELIST).
 */
export type PollsRefSource =
  | 'polls'
  | 'polls_direct'
  | 'polls_footer'
  | 'comments'
  | 'comments_direct'
  | 'comments_footer'
  | null

export interface PollsRefSourcePoll {
  pollId: string
  question: string
  totalVoters: number
  channelTitle: string | null
  createdBy: string
}

export interface PollsRefResponse {
  refSource: PollsRefSource
  refPollId: string | null
  refChannelId: string | null
  referrerID: string | null
  sourcePoll: PollsRefSourcePoll | null
  isPollsWhitelisted: boolean
}

async function fetchPollsRef(): Promise<PollsRefResponse | null> {
  try {
    const r = await fetch('/api/max/me/polls-ref', { credentials: 'include' })
    if (r.status === 401) return null // не авторизован — F трактует как «не в polls-flow»
    if (!r.ok) return null
    return (await r.json()) as PollsRefResponse
  } catch {
    return null
  }
}

/**
 * Хук для чтения `/me/polls-ref`. Кешируется React Query'ем — 5 мин staleTime,
 * т.к. ref-context статичен после `bot_started` (first-touch attribution не переписывается).
 * refetchOnWindowFocus:false — юзеру не нужно пересчитывать banner при возвращении в таб.
 */
export function usePollsRef() {
  return useQuery({
    queryKey: ['polls-ref'],
    queryFn: fetchPollsRef,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
